// TranscriptPane.tsx — the scrolling body of the open conversation: the
// pod's boot log while it isn't ready, a spinner while history loads, the
// turns themselves once it has, and the stream error under them.
//
// Hidden (not unmounted) while the workspace's Code OSS is the visible
// view, so its scroll position survives the round trip.
import React, { useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { InlineAccordion } from 'common';
import type { SessionMessage } from '../api';
import { latestReasoningIndex, liveBubbleIndex, segmentTranscript, summarizeToolRun, turnGapRem } from './chatState';
import type { SessionPhase } from './live/sessionLive';
import MessageTurn from './MessageTurn';
import type { BootLogLine } from './sessionRows';

export interface TranscriptPaneProps {
  scrollRef: React.RefObject<HTMLDivElement>;
  hidden: boolean;
  sessionPhase: SessionPhase | null;
  bootLogs: BootLogLine[];
  loadingSession: boolean;
  messages: SessionMessage[];
  selectedId: string | null;
  streaming: boolean;
  streamingId: string | null;
  streamError: string;
  /**
   * The harness's status line for the running turn — a model it is
   * waiting out, a turn it is resuming. Empty when there is none.
   */
  statusLine?: string;
}

const TranscriptPane: React.FC<TranscriptPaneProps> = ({
  scrollRef, hidden, sessionPhase, bootLogs, loadingSession, messages,
  selectedId, streaming, streamingId, streamError, statusLine,
}) => {
  // Which bubble the model is currently working on — the one that gets a
  // spinner in its corner. See chatState.ts for why it is not simply the
  // last message.
  const liveIndex = liveBubbleIndex(messages, { streaming, streamingId, selectedId });
  const newestThinking = latestReasoningIndex(messages);
  return (
    <div
      ref={scrollRef}
      className="flex-grow-1 overflow-auto p-3"
      style={{
        minHeight: 0,
        display: hidden ? 'none' : undefined,
      }}
    >
      {/* While the pod isn't Ready (or the session is suspended) there is
          no transcript to show yet — render the kubelet boot output instead
          of an empty chat placeholder so the user knows what's happening to
          the message they may already have sent. */}
      {sessionPhase !== 'ready' ? (
        <div
          style={{
            fontFamily: 'monospace',
            background: 'rgba(0,0,0,0.35)',
            border: '1px dashed var(--bs-border-color, #666)',
            borderRadius: 4,
            padding: '0.5rem 0.75rem',
            fontSize: '0.85rem',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {sessionPhase === 'suspended' ? (
            <span className="text-muted">
              Session is suspended. Click Resume to bring the pod back up.
            </span>
          ) : bootLogs.length === 0 ? (
            <span className="text-muted">Waiting for pod logs…</span>
          ) : (
            bootLogs.map((line, i) => (
              <div key={i}>
                {line.text}
                {line.count > 1 && (
                  <span className="text-muted ms-2">×{line.count}</span>
                )}
              </div>
            ))
          )}
        </div>
      ) : loadingSession ? (
        <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
      ) : messages.length === 0 ? (
        <div className="text-muted small">No messages yet. Say hello.</div>
      ) : (
        // No uniform gap: turnGapRem sizes each boundary from what sits on
        // either side of it, so a run of collapsed tool rows isn't mostly
        // whitespace.
        <div className="d-flex flex-column">
          {segmentTranscript(messages).map((seg) => {
            const turn = (i: number, gapRem: number) => (
              // Keyed by tool_call_id when the turn carries a tool UI: a
              // positional key would shift as later turns land, remounting
              // live components and dropping their sockets and scrollback.
              <MessageTurn
                key={messages[i].tool_call_id || i}
                msg={messages[i]}
                gapRem={gapRem}
                sessionId={selectedId ?? ''}
                busy={i === liveIndex}
                thinkingSuperseded={i < newestThinking}
              />
            );
            if (seg.kind === 'turn') return turn(seg.index, turnGapRem(messages, seg.index));
            const first = seg.indices[0];
            const last = seg.indices[seg.indices.length - 1];
            return (
              <ToolRun
                key={`run-${messages[first].tool_call_id || first}`}
                label={summarizeToolRun(seg.indices.map((i) => messages[i]))}
                gapRem={turnGapRem(messages, first)}
                // The run the model is still adding to: open, so its
                // progress shows, until the model says something.
                live={liveIndex !== -1 && last === messages.length - 1}
                running={seg.indices.some((i) => messages[i].tool_running)}
              >
                {seg.indices.map((i, k) => turn(i, k === 0 ? 0 : turnGapRem(messages, i)))}
              </ToolRun>
            );
          })}
        </div>
      )}
      {statusLine && (
        <div className="text-muted small mt-2" role="status">{statusLine}</div>
      )}
      {streamError && (
        <div className="alert alert-danger mt-3 mb-0 py-2 small">{streamError}</div>
      )}
    </div>
  );
};

/**
 * A run of tool calls folded into one row ("Called 3 tools, read 13
 * files"). Expanded, it shows each call's own collapsed row. Open while
 * the run is live, closed once the model moves on — unless the user
 * toggled it, after which it is theirs (as with thinking blocks).
 */
const ToolRun: React.FC<{
  label: string;
  gapRem: number;
  live: boolean;
  running: boolean;
  children: React.ReactNode;
}> = ({ label, gapRem, live, running, children }) => {
  const [open, setOpen] = useState(live);
  const touched = useRef(false);
  useEffect(() => {
    if (!touched.current) setOpen(live);
  }, [live]);
  return (
    <div style={{ marginTop: gapRem ? `${gapRem}rem` : undefined }}>
      <InlineAccordion
        open={open}
        onToggle={() => { touched.current = true; setOpen((v) => !v); }}
        header={<>{label}{running ? <span className="thinking-ellipsis" /> : null}</>}
      >
        <div className="d-flex flex-column">{children}</div>
      </InlineAccordion>
    </div>
  );
};

export default TranscriptPane;
